import React, { useState, memo } from 'react';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, useReactFlow } from '@xyflow/react';
import { IconButton, Menu, MenuItem, Typography, Box, Tooltip, TextField, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

const MAX_LENGTH = 2000;

export default memo((edgeDetails) => {
    const { id, source, sourceX, sourceY, targetX, targetY, data, intakeFormAvailable } = edgeDetails;

    const { setEdges } = useReactFlow();

    const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, targetX, targetY });

    const [anchorEl, setAnchorEl] = useState(null);

    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [textMessageAnchorEl, setTextMessageAnchorEl] = useState(null);
    const [textMessage, setTextMessage] = useState('');

    const handleTextMessageClick = (event) => {
        setTextMessageAnchorEl(event.currentTarget);
    };

    const handleTextMessageClose = () => {
        setTextMessageAnchorEl(null);
        setTextMessage('');
    };

    const [emailMessageAnchorEl, setEmailMessageAnchorEl] = useState(null);
    const [emailMessage, setEmailMessage] = useState('');

    const handleEmailMessageClick = (event) => {
        setEmailMessageAnchorEl(event.currentTarget);
    };

    const handleEmailMessageClose = () => {
        setEmailMessageAnchorEl(null);
        setEmailMessage('');
    };

    const [elapseDaysAnchorEl, setElapseDaysAnchorEl] = useState(null);
    const [elapseDays, setElapseDays] = useState('');

    const handleElapseDaysClick = (event) => {
        setElapseDaysAnchorEl(event.currentTarget);
    };

    const handleElapseDaysClose = () => {
        setElapseDaysAnchorEl(null);
        setElapseDays('');
    };

    const handleOptionClick = (option) => {
        setEdges((eds) =>
            eds.map((edge) =>
                edge.id === id ? { ...edge, data: { ...edge.data, action: option } } : edge
            )
        );
        handleClose();
    };

    const handleClearSelection = () => {
        setEdges((eds) =>
            eds.map((edge) =>
                edge.id === id ? { ...edge, data: { ...edge.data, action: null } } : edge
            )
        );
    };

    const handleDeleteEdge = () => {
        setEdges((eds) => eds.filter((edge) => edge.id !== id));
    };

    return (
        <>
            <BaseEdge id={id} path={edgePath} />
            <EdgeLabelRenderer>
                <div
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                        pointerEvents: 'all',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    {data?.action ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'orange',
                                padding: '2px 6px',
                                borderRadius: '14px',
                                fontSize: '8px',
                            }}
                        >
                            {(() => {
                                const isTextMsg = data.action.startsWith('Send A Text Message');
                                const isEmail = data.action.startsWith('Send An Email');

                                const getActionLabel = () => {
                                    return (
                                        <Typography sx={{ fontSize: '8px', color: 'black', marginRight: '4px' }}>
                                            {`${data.action.length > 40 ? data.action.substring(0, 40) + '...' : data.action}`}
                                        </Typography>
                                    );
                                };

                                if (isTextMsg || isEmail) {
                                    return (
                                        <Button
                                            variant="text"
                                            size="small"
                                            sx={{
                                                color: 'black',
                                                textTransform: 'none',
                                                padding: 0,
                                                marginRight: '4px',
                                                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                                                '&:focus': { outline: 'none', boxShadow: 'none' },
                                            }}
                                            onClick={() => {
                                                if (isTextMsg) {
                                                    setTextMessage(data.action.replace('Send A Text Message: ', ''));
                                                    setTextMessageAnchorEl(document.body);
                                                } else if (isEmail) {
                                                    setEmailMessage(data.action.replace('Send An Email: ', ''));
                                                    setEmailMessageAnchorEl(document.body);
                                                }
                                            }}
                                        >
                                            {getActionLabel()}
                                        </Button>
                                    );
                                }

                                return (
                                    getActionLabel()
                                );
                            })()}
                            <Tooltip title="Remove associated action">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSelection}
                                    sx={{
                                        width: '8px',
                                        height: '8px',
                                        color: 'black',
                                        '&:focus': { outline: 'none', boxShadow: 'none' },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '8px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Tooltip title="Associate an action">
                            <IconButton
                                size="small"
                                onClick={handleButtonClick}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: 'lightgray',
                                    border: '1px solid white',
                                    '&:hover': { backgroundColor: '#1976D2', color: 'white' },
                                    '&:focus': { outline: 'none', boxShadow: 'none' },
                                }}
                            >
                                <AddIcon sx={{ fontSize: '12px' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="Delete Edge">
                        <IconButton
                            size="small"
                            onClick={handleDeleteEdge}
                            sx={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'lightgray',
                                border: '1px solid white',
                                '&:hover': { backgroundColor: '#f44336', color: 'white' },
                                '&:focus': { outline: 'none', boxShadow: 'none' },
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: '12px' }} />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <MenuItem onClick={handleTextMessageClick}>Send A Text Message</MenuItem>
                        <MenuItem onClick={handleEmailMessageClick}>Send An Email</MenuItem>
                        <MenuItem disabled={!intakeFormAvailable} onClick={() => handleOptionClick('Send a form')}>Send Intake Form</MenuItem>
                        {source !== '0' && <MenuItem onClick={() => handleOptionClick('Task Checked-out')}>Task Checked-out</MenuItem>}
                        {source !== '0' && <MenuItem onClick={() => handleOptionClick('Manual Completion')}>Task Completed Manually</MenuItem>}
                        <MenuItem onClick={handleElapseDaysClick}>Elapse Time (Days)</MenuItem>
                    </Menu>

                    <Menu
                        anchorEl={textMessageAnchorEl}
                        open={Boolean(textMessageAnchorEl)}
                        onClose={handleTextMessageClose}
                        anchorOrigin={Boolean(anchorEl) ? { vertical: 'top', horizontal: 'right' } : { vertical: 'center', horizontal: 'center' }}
                        transformOrigin={Boolean(anchorEl) ? { vertical: 'top', horizontal: 'left' } : { vertical: 'center', horizontal: 'center' }}
                    >
                        <Box p={1} sx={{ display: 'flex', flexDirection: 'column' }} gap={1}>
                            <TextField
                                size="small"
                                label="Text Message"
                                value={textMessage}
                                onChange={(e) => setTextMessage(e.target.value)}
                                multiline
                                rows={5}
                                sx={{ width: '500px' }}
                                inputProps={{ maxLength: MAX_LENGTH }}
                            />
                            <Typography variant="caption" sx={{ alignSelf: 'flex-end', color: textMessage.length >= MAX_LENGTH ? 'red' : 'gray' }}>
                                {textMessage.length}/{MAX_LENGTH}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    handleOptionClick(`Send A Text Message: ${textMessage}`);
                                    handleTextMessageClose();
                                    handleClose();
                                }}
                                disabled={!textMessage}
                            >
                                Done
                            </Button>
                        </Box>
                    </Menu>

                    <Menu
                        anchorEl={emailMessageAnchorEl}
                        open={Boolean(emailMessageAnchorEl)}
                        onClose={handleEmailMessageClose}
                        anchorOrigin={Boolean(anchorEl) ? { vertical: 'top', horizontal: 'right' } : { vertical: 'center', horizontal: 'center' }}
                        transformOrigin={Boolean(anchorEl) ? { vertical: 'top', horizontal: 'left' } : { vertical: 'center', horizontal: 'center' }}
                    >
                        <Box p={1} sx={{ display: 'flex', flexDirection: 'column' }} gap={1}>
                            <TextField
                                size="small"
                                label="Email Body"
                                value={emailMessage}
                                onChange={(e) => setEmailMessage(e.target.value)}
                                multiline
                                rows={5}
                                sx={{ width: '500px' }}
                                inputProps={{ maxLength: MAX_LENGTH }}
                            />
                            <Typography variant="caption" sx={{ alignSelf: 'flex-end', color: emailMessage.length >= MAX_LENGTH ? 'red' : 'gray' }}>
                                {emailMessage.length}/{MAX_LENGTH}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    handleOptionClick(`Send An Email: ${emailMessage}`);
                                    handleEmailMessageClose();
                                    handleClose();
                                }}
                                disabled={!emailMessage}
                            >
                                Done
                            </Button>
                        </Box>
                    </Menu>

                    <Menu
                        anchorEl={elapseDaysAnchorEl}
                        open={Boolean(elapseDaysAnchorEl)}
                        onClose={handleElapseDaysClose}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <Box p={1}>
                            <TextField
                                size="small"
                                label="No. of Days"
                                type="number"
                                value={elapseDays}
                                onChange={(e) => setElapseDays(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && elapseDays) {
                                        handleOptionClick(`Elapse Time (Days): ${elapseDays}`);
                                        handleElapseDaysClose();
                                        handleClose();
                                    }
                                }}
                            />
                        </Box>
                    </Menu>
                </div>
            </EdgeLabelRenderer>
        </>
    );
});